import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { showToast } from '@/utils/toast';

const DEFAULT_MAX_PAGES = 200;
const ABS_MAX_PAGES = 500;
const MAX_SPU = 100;

function parseSpuInput(raw) {
  if (!raw || !String(raw).trim()) return [];
  const seen = new Set();
  const out = [];
  for (const part of String(raw).split(/[,，\s\n\r\t;；]+/)) {
    const spu = part.trim();
    if (!spu || seen.has(spu)) continue;
    seen.add(spu);
    out.push(spu);
    if (out.length >= MAX_SPU) break;
  }
  return out;
}

/**
 * 产品「同步更新」弹窗：默认全量（先商品列表再并行综合查询+详情）/ 指定 SPU。
 */
export default function SheinProductSyncModal({
  open,
  confirming = false,
  defaultMaxPages = DEFAULT_MAX_PAGES,
  onConfirm,
  onClose,
}) {
  const [mode, setMode] = useState('default'); // default | custom
  const [maxPages, setMaxPages] = useState(String(defaultMaxPages));
  const [spuText, setSpuText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setMode('default');
    setMaxPages(String(defaultMaxPages || DEFAULT_MAX_PAGES));
    setSpuText('');
  }, [open, defaultMaxPages]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !confirming && !submitting) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirming, submitting, onClose]);

  const spuNames = useMemo(() => parseSpuInput(spuText), [spuText]);

  if (!open) return null;

  const busy = confirming || submitting;

  const handleConfirm = async () => {
    if (confirming || submittingRef.current) return;
    if (mode === 'custom') {
      if (spuNames.length === 0) {
        showToast(fixedUi("指定同步请填写至少一个 SPU"), 'error');
        return;
      }
      if (spuNames.length > MAX_SPU) {
        showToast(`最多指定 ${MAX_SPU} 个 SPU`, 'error');
        return;
      }
      submittingRef.current = true;
      setSubmitting(true);
      try {
        await onConfirm?.({
          maxPages: Math.min(ABS_MAX_PAGES, Math.max(1, Number(defaultMaxPages) || DEFAULT_MAX_PAGES)),
          spuNames,
        });
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
      return;
    }

    const pages = Number(maxPages);
    if (!Number.isFinite(pages) || pages < 1) {
      showToast(fixedUi("请填写有效的同步分页数（≥1）"), 'error');
      return;
    }
    if (pages > ABS_MAX_PAGES) {
      showToast(`${fixedUi('同步分页数最多')} ${ABS_MAX_PAGES}`, 'error');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm?.({
        maxPages: Math.min(ABS_MAX_PAGES, Math.max(1, Math.floor(pages))),
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="spo-batch-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={fixedUi("关闭")}
        disabled={busy}
        onClick={() => !busy && onClose?.()}
      />
      <div
        className="spo-batch-modal__panel spo-sync-range-modal__panel spo-product-sync-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-product-sync-modal-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-product-sync-modal-title">{fixedUi("产品同步更新")}</h2>
          <button
            type="button"
            className="spo-btn spo-btn--ghost spo-btn--sm"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("关闭")}
          </button>
        </div>
        <div className="spo-batch-modal__body">
          <div className="spo-sync-range-modal__presets" role="group" aria-label={fixedUi("同步方式")}>
            <button
              type="button"
              className={`spo-btn spo-btn--sm ${mode === 'default' ? 'spo-btn--primary' : 'spo-btn--secondary'}`}
              disabled={busy}
              onClick={() => setMode('default')}
            >
              {fixedUi("默认全量")}
            </button>
            <button
              type="button"
              className={`spo-btn spo-btn--sm ${mode === 'custom' ? 'spo-btn--primary' : 'spo-btn--secondary'}`}
              disabled={busy}
              onClick={() => setMode('custom')}
            >
              指定同步
            </button>
          </div>
          <div className="spo-sync-range-modal__fields">
            {mode === 'default' ? (
              <label className="spo-field spo-field--block">
                <span className="spo-field__label">{fixedUi("同步分页数（商品列表 pageSize=200，页数上限）")}</span>
                <input
                  className="spo-input spo-product-sync-modal__input"
                  type="number"
                  min={1}
                  max={ABS_MAX_PAGES}
                  step={1}
                  value={maxPages}
                  disabled={busy}
                  onChange={(e) => setMaxPages(e.target.value)}
                />
              </label>
            ) : (
              <label className="spo-field spo-field--block">
                <span className="spo-product-sync-modal__label-row">
                  <span className="spo-field__label">SPU 列表（必填，最多 {MAX_SPU} 个）</span>
                  <span className="spo-product-sync-modal__count">{spuNames.length}/{MAX_SPU}</span>
                </span>
                <textarea
                  className="spo-input spo-product-sync-modal__input spo-product-sync-modal__spu"
                  rows={6}
                  placeholder={fixedUi("多个 SPU 用换行、逗号或空格分隔")}
                  value={spuText}
                  required
                  aria-required="true"
                  disabled={busy}
                  onChange={(e) => setSpuText(e.target.value)}
                />
              </label>
            )}
          </div>
        </div>
        <div className="spo-batch-modal__foot">
          <button
            type="button"
            className="spo-btn spo-btn--ghost"
            disabled={busy}
            onClick={onClose}
          >
            {fixedUi("取消")}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary"
            disabled={busy || (mode === 'custom' && spuNames.length === 0)}
            onClick={handleConfirm}
          >
            {busy ? fixedUi("启动中…") : fixedUi("开始同步")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
